import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { getUser } from "../../../../pages/userSlice";

import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { CODES } from "../../../../consts/codes";
import ModalInfo from "../../Modals/modalInfo";
import { REGEXP } from "../../../../consts/regexp";

import { NrpForm } from "./nrpForm";

import { UpdateNrp } from "../../../../services/user/infonavit/adminNrps";

const EditNrp = ({
  handleFetch,
  openEditNrp,
  setOpenEditNrp,
  setNrpData,
  nrpData = null,
}) => {
  /**
   * Use Selector
   */
  const userInfo = useSelector(getUser);

  /**
   * Title Container component
   */
  const DialogTitleContainer = (props) => {
    const { children, onClose, ...other } = props;

    return (
      <DialogTitle {...other}>
        {children}
        {onClose ? (
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        ) : null}
      </DialogTitle>
    );
  };

  /**
   * useTranslation
   */
  const { t } = useTranslation();

  /**
   * Schema
   */
  const schema = yup.object().shape({
    nrp: yup
      .string()
      .min(11, t("App.validationMessages.minCharacters", { number: 11 }))
      .max(11, t("App.validationMessages.maxCharacters", { number: 11 }))
      .required(t("App.validationMessages.required"))
      .matches(REGEXP.INFONAVIT.NRP, t("App.validationMessages.infonavit.nrp")),
  });

  /**
   * useForm
   */
  const {
    handleSubmit,
    setValue,
    reset,
    getValues,
    watch,
    formState: { errors },
    control,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      nrp: "",
      state: true,
    },
  });

  /**
   * Use State
   */
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [isDesktop, setDesktop] = useState(window.innerWidth > 576);

  const [openModalInfo, setOpenModalInfo] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [editNrpData, setEditNrpData] = useState({});

  /**
   * Use Effect
   */

  /**
   * useEffect para actualizar el tamaño de pantalla actual de la pantalla
   */
  useEffect(() => {
    window.addEventListener("resize", updateMedia);
    return () => window.removeEventListener("resize", updateMedia);
  });

  /**
   * Carga la información de las credenciales a editar
   */
  useEffect(() => {
    setValue("nrp", nrpData?.nrp);
    setValue("state", nrpData?.state);
  }, [nrpData]);

  /**
   * Handles
   */

  /**
   * Función para cerrar el modal
   */
  const handleClose = async () => {
    setOpenEditNrp(false);

    setNrpData({});

    setLoadingCreate(false);
    reset();
  };

  /**
   * Método para actualizar el tamaño de pantalla actual
   */
  const updateMedia = () => {
    setDesktop(window.innerWidth > 490);
  };

  /**
   *
   * Función que construye el objeto de usuario
   *
   * @param {Object} data información del usuario
   * @returns objeto de usuario según formato
   */
  const handleEditNrpPayload = ({ state }) => {
    const payload = {
      ...nrpData,
      state,
    };

    return payload;
  };

  /**
   * Función que retorna el título del modal según código de respuesta
   * @param {Number} responseCode código de respuesta
   * @returns título según código
   */
  const handleModalTitle = (responseCode) => {
    if (responseCode === CODES.S_COD_RESPONSE_SUCCESS_REQUEST) {
      return "";
    } else if (responseCode === 1001) {
      return t("App.userDetail.modalTitle.title2");
    } else if (responseCode === 1002) {
      return t("App.userDetail.modalTitle.title1");
    } else {
      return "";
    }
  };

  /**
   *
   * Function to edit nrp
   *
   * @param {Object} data
   */
  const handleEditNrp = async (data) => {
    try {
      setLoadingCreate(true);

      const nrpPayload = handleEditNrpPayload(data);
      const {
        status,
        data: { responseStatus, response },
      } = await UpdateNrp(nrpPayload);

      if (
        status === CODES.COD_RESPONSE_HTTP_OK &&
        responseStatus.statusCode === CODES.S_COD_RESPONSE_SUCCESS_REQUEST
      ) {
        handleFetch();
      }

      setModalTitle(handleModalTitle(responseStatus.statusCode));
      setOpenModalInfo(true);
      setEditNrpData({ data: { responseStatus, response }, status });
    } catch (error) {
      console.log("====================================");
      console.log(error);
      console.log("====================================");
    } finally {
      setLoadingCreate(false);
    }
  };

  return (
    <>
      <Dialog
        onClose={handleClose}
        open={openEditNrp}
        sx={{
          "& .MuiDialog-container": {
            "& .MuiPaper-root": {
              width: "100%",
              height: "min-content",
              maxWidth: "900px",
              maxHeight: "90%",
              boxShadow: "none",
              borderRadius: "22px",
              padding: "2rem",
            },
          },
        }}
      >
        <DialogTitleContainer onClose={handleClose}>
          <Grid container direction="column" rowSpacing={1}>
            <Grid item className="display-large__primary-one">
              {t("App.infonavitEmployerRecords.formModal.editTitle")}
            </Grid>
          </Grid>
        </DialogTitleContainer>

        <DialogContent className="scroll-table">
          <form id="form-nrp-edit" onSubmit={handleSubmit(handleEditNrp)}>
            <NrpForm control={control} errors={errors} t={t} />
          </form>
        </DialogContent>

        <DialogActions>
          <Grid
            container
            direction={isDesktop ? "row" : "column-reverse"}
            className="col-flex"
            columnGap={4}
            sx={{ padding: "1.5%" }}
          >
            <Grid item xs={12} sm={2} lg={1} className="full-width">
              <Button
                className="btn__filled__gradient full-width"
                onClick={handleClose}
              >
                {t("App.buttonLabels.cancel")}
              </Button>
            </Grid>
            <Grid item xs={12} sm={4} lg={2.2} className="full-width">
              <Button
                className="btn__applyFilter"
                type="submit"
                form="form-nrp-edit"
                startIcon={!loadingCreate ? <SaveIcon /> : <></>}
              >
                {!loadingCreate ? (
                  t("App.infonavitEmployerRecords.formModal.editBtn")
                ) : (
                  <CircularProgress size={21} color="inherit" />
                )}
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>

      <ModalInfo
        title={modalTitle}
        responseData={editNrpData}
        onClose={() => {
          handleClose();
          setOpenModalInfo(false);
        }}
        open={openModalInfo}
      />
    </>
  );
};

export default EditNrp;
